/**
 * contract-verdict.ts — the verdict-honesty classifier.
 *
 * This is the precondition that makes a *required* CI check safe to turn on:
 * "blocking is earned." A canonical-bypass occurrence may block a merge ONLY
 * when every trust gate holds at once —
 *
 *   1. it is a canonical-usage rule (the family that steers to a primitive),
 *   2. the tenant has a CONFIRMED canonical mapping for the suggested primitive
 *      (never `proposed`/`unknown` — an unconfirmed mapping cannot block),
 *   3. the finding's source location is VERIFIED against the PR head blob
 *      (we reproduced it; it is not a stale or hallucinated location), and
 *   4. the occurrence is INTRODUCED by this PR (not a pre-existing bypass on a
 *      line nobody touched).
 *
 * Anything short of all four is `advisory` (reported, never blocking), and an
 * active waiver makes it `suppressed`. Pre-existing bypasses are advisory by
 * construction, so turning the gate on never retroactively red-Xes open PRs —
 * the single fastest way to lose a team's trust permanently.
 *
 * The cardinal guard: a blocking occurrence ALWAYS carries a nameable
 * remediation. Gate (2) guarantees it — a confirmed mapping *is* the named fix
 * (use `<Button>` from `@acme/ui`). The classifier never blocks on something it
 * cannot tell the developer how to fix.
 *
 * Pure and side-effect-free: callers gather the four signals from persisted
 * state and feed them in. No GitHub, no Convex — fully unit-testable.
 */

export type VerdictTier = 'blocking' | 'advisory' | 'suppressed';

/** The category emitted by the canonical-usage rule family (FUI1001–FUI1004). */
export const CANONICAL_USAGE_CATEGORY = 'canonical-usage';

/**
 * Why an occurrence that *would otherwise* have blocked was downgraded to
 * advisory — a closed vocabulary, recorded on the occurrence (or verdict) and
 * surfaced on the Check Run + dashboard so a fail-open is never silent. The
 * gate never blocks on uncertainty, but uncertainty is always visible.
 *
 *  - `source_unverified` — the finding location could not be verified against
 *    the PR head (a genuine non-reproduction, or a GitHub read that failed open)
 *  - `mapping_proposed`  — a canonical mapping exists but is only *proposed*,
 *    not confirmed, so there is no agreed fix to offer
 *  - `mapping_unknown`   — no canonical mapping at all for the suggested primitive
 *  - `not_introduced`    — pre-existing bypass, not introduced by this PR
 *  - `contract_unpinned` — no contract FCID could be resolved (verdict-level)
 *  - `signal_error`      — the blocking signals could not be gathered; fail open
 */
export type DowngradeReason =
  | 'source_unverified'
  | 'mapping_proposed'
  | 'mapping_unknown'
  | 'not_introduced'
  | 'contract_unpinned'
  | 'signal_error';

export interface VerdictOccurrenceInput {
  fingerprint: string;
  ruleId: string;
  code?: string;
  category?: string;
  filePath?: string;
  line?: number;
  /** The canonical primitive the bypass should have used (e.g. "Button"). */
  suggestedComponentName?: string;
  /** The canonical import source (e.g. "@acme/ui"). */
  suggestedFrom?: string;
  /** A confirmed canonical mapping exists for `suggestedComponentName`. */
  hasConfirmedMapping: boolean;
  /** The finding location was reproduced against the PR head blob. */
  sourceVerified: boolean;
  /** Introduced by this PR (vs pre-existing/persistent). */
  introduced: boolean;
  /** An active waiver suppresses this occurrence. */
  suppressed: boolean;
  /** A deterministic structural fix is available (for messaging only). */
  deterministicFixAvailable?: boolean;
  /**
   * Effective canonical-mapping status for the suggested primitive, used only
   * to tell a *proposed* mapping (a guess we won't block on) apart from no
   * mapping at all. Gate (2) still keys on `hasConfirmedMapping`.
   */
  mappingStatus?: 'confirmed' | 'proposed' | 'unknown';
  /**
   * The blocking signals for this occurrence could not be gathered (e.g. a
   * corrupt or unreadable signal). Forces a fail-open downgrade with
   * `signal_error` instead of crashing or silently dropping the occurrence.
   */
  signalError?: boolean;
}

export interface ClassifiedOccurrence {
  fingerprint: string;
  ruleId: string;
  code?: string;
  filePath?: string;
  line?: number;
  suggestedComponentName?: string;
  suggestedFrom?: string;
  tier: VerdictTier;
  /** Human-readable justification for the tier (drives the check output + audit). */
  reasons: string[];
  /**
   * Structured fail-open reason for an occurrence that *would otherwise* have
   * blocked but was downgraded to advisory — surfaced + aggregated. Undefined
   * for blocking, suppressed, or never-eligible (non-canonical) occurrences.
   */
  downgradeReason?: DowngradeReason;
  deterministicFixAvailable: boolean;
}

/**
 * The contract version a verdict was evaluated against — the proof of
 * "served == enforced." Resolved from the governance run's pinned contract
 * version by the caller and stamped onto the verdict here, so every gate
 * decision can name the exact FCID it judged against. All fields optional: a
 * run with no resolvable contract version yields an *unpinned* verdict (the
 * gate must never block without naming a contract — surfaced in brief 03).
 */
export interface ContractVerdictPin {
  /** 64-hex FCID the scan was evaluated against. */
  evaluatedContractHash?: string;
  /** Monotonic contract version number for display ("evaluated against vN"). */
  evaluatedContractVersion?: number;
  /** The scanned FCID is no longer the latest published one for its binding. */
  contractStale?: boolean;
}

export interface ContractVerdict extends ContractVerdictPin {
  conclusion: 'success' | 'failure' | 'neutral';
  /**
   * No contract FCID could be resolved for this verdict (`contract_unpinned`).
   * The gate must be able to name the contract it judged against; an unpinned
   * verdict is surfaced, never silently enforced. Derived from the pin.
   */
  contractUnpinned: boolean;
  occurrences: ClassifiedOccurrence[];
  blocking: ClassifiedOccurrence[];
  advisory: ClassifiedOccurrence[];
  suppressed: ClassifiedOccurrence[];
  counts: {
    blocking: number;
    advisory: number;
    suppressed: number;
    total: number;
  };
}

/**
 * Whether an occurrence belongs to the canonical-usage family. Keyed on the
 * rule category, with `suggestedComponentName` as a fallback signal (only
 * canonical-steering rules populate it).
 */
export function isCanonicalUsageOccurrence(
  occurrence: Pick<
    VerdictOccurrenceInput,
    'category' | 'suggestedComponentName'
  >,
): boolean {
  return (
    occurrence.category === CANONICAL_USAGE_CATEGORY ||
    Boolean(occurrence.suggestedComponentName)
  );
}

/**
 * The single structured fail-open reason for a downgraded occurrence, in
 * priority order from the most fundamental doubt to the most specific missing
 * precondition: `signal_error` (couldn't gather the signals) → `source_unverified`
 * (can't trust the location) → `not_introduced` (pre-existing) → mapping (no
 * confirmed fix to offer). A non-canonical occurrence was never
 * blocking-eligible, so it has no fail-open reason. `contract_unpinned` is a
 * verdict-level reason, set in `buildContractVerdict`, not here.
 */
function downgradeReasonFor(
  occurrence: VerdictOccurrenceInput,
  canonicalUsage: boolean,
): DowngradeReason | undefined {
  if (occurrence.signalError) return 'signal_error';
  if (!canonicalUsage) return undefined;
  if (!occurrence.sourceVerified) return 'source_unverified';
  if (!occurrence.introduced) return 'not_introduced';
  if (!occurrence.hasConfirmedMapping) {
    return occurrence.mappingStatus === 'proposed'
      ? 'mapping_proposed'
      : 'mapping_unknown';
  }
  return undefined;
}

export function classifyOccurrence(
  occurrence: VerdictOccurrenceInput,
): ClassifiedOccurrence {
  const base: Omit<ClassifiedOccurrence, 'tier' | 'reasons'> = {
    fingerprint: occurrence.fingerprint,
    ruleId: occurrence.ruleId,
    code: occurrence.code,
    filePath: occurrence.filePath,
    line: occurrence.line,
    suggestedComponentName: occurrence.suggestedComponentName,
    suggestedFrom: occurrence.suggestedFrom,
    deterministicFixAvailable: occurrence.deterministicFixAvailable ?? false,
  };

  if (occurrence.suppressed) {
    return {
      ...base,
      tier: 'suppressed',
      reasons: ['an active waiver suppresses this occurrence'],
    };
  }

  const canonicalUsage = isCanonicalUsageOccurrence(occurrence);
  const blockedReasons: string[] = [];
  if (occurrence.signalError) {
    blockedReasons.push(
      'blocking signals could not be gathered — failing open',
    );
  }
  if (!canonicalUsage) {
    blockedReasons.push('not a canonical-usage rule');
  }
  if (!occurrence.hasConfirmedMapping) {
    blockedReasons.push(
      'no confirmed canonical mapping for the suggested primitive',
    );
  }
  if (!occurrence.sourceVerified) {
    blockedReasons.push('source location not verified against the PR head');
  }
  if (!occurrence.introduced) {
    blockedReasons.push('pre-existing — not introduced by this PR');
  }

  if (blockedReasons.length === 0) {
    const target = occurrence.suggestedComponentName
      ? ` of the confirmed primitive ${occurrence.suggestedComponentName}` +
        (occurrence.suggestedFrom ? ` (${occurrence.suggestedFrom})` : '')
      : ' of a confirmed primitive';
    return {
      ...base,
      tier: 'blocking',
      reasons: [
        `introduced canonical bypass${target}, verified at the PR head`,
      ],
    };
  }

  return {
    ...base,
    tier: 'advisory',
    reasons: blockedReasons,
    downgradeReason: downgradeReasonFor(occurrence, canonicalUsage),
  };
}

/**
 * Build the contract verdict for a set of occurrences. The check conclusion is
 * `failure` iff at least one unwaived blocking occurrence exists; `neutral` if
 * there is anything advisory/suppressed but nothing blocking; `success` when
 * clean. A neutral conclusion never blocks a required check.
 *
 * `pin` carries the contract version this scan was evaluated against (resolved
 * by the caller from the run's pinned contract). It is echoed onto the verdict
 * so the gate decision names its contract FCID; it never affects the
 * classification — the conclusion is a pure function of the occurrences.
 */
export function buildContractVerdict(
  occurrences: VerdictOccurrenceInput[],
  pin: ContractVerdictPin = {},
): ContractVerdict {
  const classified = occurrences.map(classifyOccurrence);
  const blocking = classified.filter((o) => o.tier === 'blocking');
  const advisory = classified.filter((o) => o.tier === 'advisory');
  const suppressed = classified.filter((o) => o.tier === 'suppressed');

  const conclusion: ContractVerdict['conclusion'] =
    blocking.length > 0
      ? 'failure'
      : advisory.length > 0 || suppressed.length > 0
        ? 'neutral'
        : 'success';

  return {
    ...pin,
    conclusion,
    contractUnpinned: !pin.evaluatedContractHash,
    occurrences: classified,
    blocking,
    advisory,
    suppressed,
    counts: {
      blocking: blocking.length,
      advisory: advisory.length,
      suppressed: suppressed.length,
      total: classified.length,
    },
  };
}

/**
 * Aggregate the fail-open downgrades on a verdict — the single source of truth
 * for the Check Run "failed open" surfacing, the per-ingest telemetry event, and
 * the dashboard surface (brief 03). `unverified` counts the occurrences the gate
 * could not check (`source_unverified` + `signal_error`); `byReason` carries the
 * full breakdown. A pure function of the verdict — no IO.
 */
export interface FailOpenSummary {
  /** Occurrences that could not be verified (source_unverified + signal_error). */
  unverified: number;
  /** Total advisory occurrences carrying a structured fail-open reason. */
  downgraded: number;
  /** Count per downgrade reason (only non-zero entries present). */
  byReason: Partial<Record<DowngradeReason, number>>;
  /** No contract FCID could be resolved for this verdict. */
  contractUnpinned: boolean;
}

export function failOpenSummary(verdict: ContractVerdict): FailOpenSummary {
  const byReason: Partial<Record<DowngradeReason, number>> = {};
  let downgraded = 0;
  for (const occurrence of verdict.advisory) {
    const reason = occurrence.downgradeReason;
    if (!reason) continue;
    downgraded += 1;
    byReason[reason] = (byReason[reason] ?? 0) + 1;
  }
  const unverified =
    (byReason.source_unverified ?? 0) + (byReason.signal_error ?? 0);
  return {
    unverified,
    downgraded,
    byReason,
    contractUnpinned: verdict.contractUnpinned,
  };
}
